// frontend/src/pages/InvoicesPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";


const statoLabel = (s) => {
  const up = (s || "").toUpperCase();
  if (up === "DRAFT") return "Bozza";
  if (up === "ISSUED") return "Proforma";
  if (up === "SENT") return "Inviata";
  if (up === "PAID") return "Pagata";
  return s || "—";
};

const API_URL = process.env.REACT_APP_API_URL || "";

const currentMonthValue = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

// Badge per stato
const badgeClass = (st) => {
  const s = (st || "").toUpperCase();
  if (s === "DRAFT") return "badge bg-secondary";
  if (s === "ISSUED") return "badge bg-info text-dark";
  if (s === "SENT") return "badge bg-primary";
  if (s === "PAID") return "badge bg-success";
  if (s === "CANCELED") return "badge bg-dark";
  return "badge bg-light text-dark";
};

// parser robusto per liste (array o paginato)
function parseListResponse(text) {
  let data;
  try { data = text ? JSON.parse(text) : []; } catch { data = []; }
  const items = Array.isArray(data) ? data : data.results || data.items || [];
  const count = typeof data?.count === "number" ? data.count : items.length;
  const next = data?.next || null;
  const previous = data?.previous || null;
  return { items, count, next, previous };
}

// ──────────────────────────────────────────────────────────────────────────────
// Modal semplice (no Bootstrap JS), solo CSS bootstrap-like
function Modal({ title, onClose, children, footer }) {
  return (
    <div
      className="position-fixed top-0 start-0 w-100 h-100"
      style={{ background: "rgba(0,0,0,0.35)", zIndex: 1050 }}
      onClick={onClose}
    >
      <div
        className="modal-dialog modal-lg modal-dialog-centered"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 980, width: "100%" }}
      >
        <div className="modal-content shadow">
          <div className="modal-header">
            <h5 className="modal-title m-0">{title}</h5>
            <button type="button" className="btn-close" onClick={onClose} />
          </div>
          <div className="modal-body">{children}</div>
          {footer && <div className="modal-footer">{footer}</div>}
        </div>
      </div>
    </div>
  );
}
// ──────────────────────────────────────────────────────────────────────────────

export default function InvoicesPage() {
  const nav = useNavigate();

  const [role, setRole] = useState("");
  const isMadre = role === "MADRE";

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // tabella proforme
  const [list, setList] = useState([]);
  const [count, setCount] = useState(0);

  // filtri
  const [q, setQ] = useState("");
  const [month, setMonth] = useState(currentMonthValue());
  const [status, setStatus] = useState("");
  const [docType, setDocType] = useState(""); // TD01/TD04
  const [onlyUnpaid, setOnlyUnpaid] = useState(false);
  const [onlyPublished, setOnlyPublished] = useState(false);

  // paginazione
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // modale ordini → proforma (endpoint rapido /api/ordini/ricevuti/)
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [orders, setOrders] = useState([]);
  const [ordersCount, setOrdersCount] = useState(0);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [oQ, setOQ] = useState("");
  const [oStatus, setOStatus] = useState("");
  const [oPage, setOPage] = useState(1);
  const [oPageSize, setOPageSize] = useState(10);
  const [oHasMore, setOHasMore] = useState(false);


  const formatDateIT = (value) => {
    if (!value) return "—";
    const m = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return `${m[3]}/${m[2]}/${m[1]}`;
    return String(value);
  };

  const token = localStorage.getItem("access");
  const headers = useMemo(
    () => ({
      Authorization: token ? `Bearer ${token}` : "",
      "Content-Type": "application/json",
    }),
    [token]
  );

  // ruolo utente
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/me/`, { headers });
        const me = await r.json();
        setRole((me?.role || "").toUpperCase());
      } catch {
        setRole("");
      }
    })();
  }, [headers]);

  // caricamento lista
  function buildInvoicesUrl(overrides = {}) {
    const v = {
      month,
      q,
      status,
      docType,
      onlyUnpaid,
      onlyPublished,
      page,
      pageSize,
      ...overrides,
    };

    const url = new URL(`${API_URL}/api/fatture/`);
    if (v.month) url.searchParams.set("month", v.month);
    if (v.q) url.searchParams.set("q", v.q);
    if (v.status) url.searchParams.set("status", v.status);
    if (v.docType) url.searchParams.set("tipo_documento", v.docType);
    if (v.onlyUnpaid) url.searchParams.set("is_paid", "false");
    if (v.onlyPublished) url.searchParams.set("is_published", "true");
    url.searchParams.set("page", String(v.page));
    url.searchParams.set("page_size", String(v.pageSize));
    return url;
  }

  async function load(overrides = {}) {
    setLoading(true);
    setError("");
    try {
      const r = await fetch(buildInvoicesUrl(overrides), { headers });
      const t = await r.text();
      const { items, count } = parseListResponse(t);
      setList(items);
      setCount(count || items.length || 0);
    } catch {
      setList([]);
      setCount(0);
      setError("Impossibile caricare le proforme.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // KPI pagina corrente
  const kpi = useMemo(() => {
    const tot = (list || []).reduce((acc, f) => acc + Number(f.total || 0), 0);
    const nonPag = (list || []).filter((f) => !f.is_paid).length;
    const bozze = (list || []).filter((f) => (f.status || "").toUpperCase() === "DRAFT").length;
    return { totalePagina: tot, nonPagatePagina: nonPag, bozzePagina: bozze };
  }, [list]);

  // ───────── proforme: azioni
  async function createDraft() {
    try {
      const r = await fetch(`${API_URL}/api/fatture/`, {
        method: "POST",
        headers,
        body: JSON.stringify({ status: "DRAFT", series: "A", tipo_documento: "TD01" }),
      });
      const data = await r.json();
      if (r.ok && data?.id) nav(`/fatture/${data.id}`);
      else throw new Error(data?.detail || "Errore creazione bozza");
    } catch (e) {
      alert("❌ " + (e.message || "Errore creazione bozza proforma"));
    }
  }

  async function deleteInvoice(inv) {
    if (!inv?.id) return;
    if ((inv.status || "").toUpperCase() !== "DRAFT") {
      alert("Solo le bozze possono essere eliminate.");
      return;
    }
    if (!window.confirm(`Eliminare la bozza #${inv.id} ${inv.series}/${inv.number ?? "—"}?`)) return;
    try {
      const r = await fetch(`${API_URL}/api/fatture/${inv.id}/`, { method: "DELETE", headers });
      if (!r.ok) {
        let d = {};
        try { d = await r.json(); } catch {}
        throw new Error(d?.detail || "Impossibile eliminare");
      }
      await load();
    } catch (e) {
      alert("❌ " + (e.message || "Errore eliminazione"));
    }
  }

  function exportCsv() {
    const rows = [
      ["ID", "SERIE", "NUMERO", "DATA", "TIPO_DOC", "RAGIONE_SOCIALE", "TOTALE", "STATO", "PAGATA", "PUBBLICATA"],
      ...list.map((f) => [
        f.id,
        f.series || "",
        f.number ?? "",
        f.date || "",
        f.tipo_documento || "",
        f.fatt_ragione_sociale || "",
        Number(f.total || 0).toFixed(2),
        f.status || "",
        f.is_paid ? "Sì" : "No",
        f.is_published ? "Sì" : "No",
      ]),
    ]
      .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(";"))
      .join("\n");

    const blob = new Blob([rows], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `proforme_pagina_${page}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  const applyFilters = () => {
    setPage(1);
    load({ page: 1 });
  };

  const resetFilters = () => {
    const m = currentMonthValue();
    setQ("");
    setMonth(m);
    setStatus("");
    setDocType("");
    setOnlyUnpaid(false);
    setOnlyPublished(false);
    setPage(1);
    load({
      q: "",
      month: m,
      status: "",
      docType: "",
      onlyUnpaid: false,
      onlyPublished: false,
      page: 1,
    });
  };

  // ───────── ordini → proforma (quickpick)
  function buildOrdersQuickUrl() {
    const url = new URL(`${API_URL}/api/ordini/ricevuti/`);
    if (oQ) url.searchParams.set("q", oQ);
    if (oStatus) url.searchParams.set("status", String(oStatus).toUpperCase());
    url.searchParams.set("page", String(oPage));
    url.searchParams.set("page_size", String(oPageSize));
    url.searchParams.set("order_by", "-id");
    return url;
  }

  async function loadOrders({ retryIfEmpty = true } = {}) {
    setOrdersLoading(true);
    try {
      const r = await fetch(buildOrdersQuickUrl(), { headers: { Authorization: headers.Authorization } });
      const t = await r.text();
      const data = t ? JSON.parse(t) : { results: [] };
      const items = Array.isArray(data) ? data : (data.results || []);
      const total = typeof data?.count === "number" ? data.count : items.length;
      const next = data?.next || null;

      if (oPage > 1 && items.length === 0 && total > 0) {
        setOPage(1);
        setOrdersLoading(false);
        setTimeout(() => loadOrders({ retryIfEmpty }), 0);
        return;
      }
      if (retryIfEmpty && oStatus && items.length === 0) {
        setOStatus("");
        setOPage(1);
        setOrdersLoading(false);
        setTimeout(() => loadOrders({ retryIfEmpty: false }), 0);
        return;
      }

      setOrders(items);
      setOrdersCount(total);
      setOHasMore(Boolean(next));
    } catch {
      setOrders([]);
      setOrdersCount(0);
      setOHasMore(false);
    } finally {
      setOrdersLoading(false);
    }
  }

  function openOrdersModal() {
    setShowOrderModal(true);
    setOPage(1);
    setTimeout(() => loadOrders(), 0);
  }

  async function createFromOrder(orderId) {
    if (!orderId) return;
    try {
        const r = await fetch(`${API_URL}/api/fatture/da-ordine/`, {
          method: "POST",
          headers: { ...headers, "Content-Type": "application/json" },
          body: JSON.stringify({ order_id: orderId, series: "PF" }),
        });
        const data = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(data?.detail || "Errore creazione da ordine");
        if (data?.id) {
          setShowOrderModal(false);
            nav(`/fatture/${data.id}`);
      }
    } catch (e) {
      alert("❌ " + (e.message || "Errore in creazione da ordine"));
    }
  }

  useEffect(() => {
    if (showOrderModal) loadOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showOrderModal, oPage, oPageSize]);

  useEffect(() => {
    if (!showOrderModal) return;
    const t = setTimeout(() => loadOrders(), 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [oQ, oStatus]);

  // ───────────────────────────────────────────────────────────────────────────
  return (
    <div className="container py-3">
      {/* Header + azioni */}
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
        <h2 className="m-0">🧾 Proforma</h2>

        <div className="d-flex flex-wrap gap-2">
          <Link to="/" className="btn btn-outline-secondary">⟵ Menu principale</Link>
          <button className="btn btn-primary" onClick={createDraft}>➕ Nuova proforma (bozza)</button>
          <button className="btn btn-outline-primary" onClick={openOrdersModal}>➕ Da Ordine…</button>
          <button className="btn btn-outline-secondary" onClick={load}>🔄 Aggiorna</button>
          <button className="btn btn-outline-dark" onClick={exportCsv}>⤓ Esporta CSV (pagina)</button>
        </div>
      </div>

      {/* KPI rapidi */}
      <div className="row g-3 mb-3">
        <div className="col-12 col-md-4">
          <div className="card shadow-sm">
            <div className="card-body d-flex justify-content-between align-items-center">
              <div>
                <div className="small text-muted">Totale (pagina)</div>
                <div className="h3 m-0">€ {kpi.totalePagina.toFixed(2)}</div>
              </div>
              <div style={{ fontSize: 28 }}>💶</div>
            </div>
          </div>
        </div>
        <div className="col-6 col-md-4">
          <div className="card shadow-sm">
            <div className="card-body d-flex justify-content-between align-items-center">
              <div>
                <div className="small text-muted">Non pagate (pagina)</div>
                <div className="h3 m-0">{kpi.nonPagatePagina}</div>
              </div>
              <div style={{ fontSize: 28 }}>⏳</div>
            </div>
          </div>
        </div>
        <div className="col-6 col-md-4">
          <div className="card shadow-sm">
            <div className="card-body d-flex justify-content-between align-items-center">
              <div>
                <div className="small text-muted">Bozze (pagina)</div>
                <div className="h3 m-0">{kpi.bozzePagina}</div>
              </div>
              <div style={{ fontSize: 28 }}>📝</div>
            </div>
          </div>
        </div>
      </div>

      {/* Filtri */}
      <div className="card shadow-sm mb-3">
        <div className="card-body">
          <div className="row g-2 align-items-end">
              <div className="col-6 col-md-2">
                <label className="form-label small text-muted">Mese</label>
                <input
                  type="month"
                  className="form-control"
                  value={month}
                  onChange={(e) => {
                      const m = e.target.value;
                      setMonth(m);
                      setPage(1);
                      load({ month: m, page: 1 });
                    }}
                />
              </div>
            <div className="col-12 col-md-4">
              <label className="form-label small text-muted">Ricerca</label>
              <input
                className="form-control"
                placeholder="Nome cliente / numero / data / email"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && applyFilters()}
              />
            </div>
            <div className="col-6 col-md-2">
              <label className="form-label small text-muted">Stato</label>
              <select className="form-select" value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="">Tutti</option>
                <option value="DRAFT">Bozza</option>
                <option value="ISSUED">Emessa</option>
                <option value="SENT">Inviata</option>
                <option value="PAID">Pagata</option>
                <option value="CANCELED">Annullata</option>
              </select>
            </div>
            <div className="col-6 col-md-2">
              <label className="form-label small text-muted">Tipo Doc.</label>
              <select className="form-select" value={docType} onChange={(e) => setDocType(e.target.value)}>
                <option value="">Tutti</option>
                <option value="TD01">Proforma (TD01)</option>
                <option value="TD04">Nota di credito (TD04)</option>
              </select>
            </div>
            <div className="col-6 col-md-2">
              <label className="form-label small text-muted">Per pagina</label>
              <select
                className="form-select"
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value) || 20);
                  setPage(1);
                  setTimeout(load, 0);
                }}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
            <div className="col-12 col-md-2 d-flex gap-2">
              <button className="btn btn-secondary w-100" onClick={applyFilters}>Applica</button>
              <button className="btn btn-outline-secondary w-100" onClick={resetFilters}>Reset</button>
            </div>
          </div>

          <div className="row g-2 mt-2">
            <div className="col-6 col-md-3 form-check">
              <input
                id="f-unpaid"
                className="form-check-input"
                type="checkbox"
                checked={onlyUnpaid}
                onChange={(e) => setOnlyUnpaid(e.target.checked)}
              />
              <label htmlFor="f-unpaid" className="form-check-label">Solo non pagate</label>
            </div>
            {isMadre && (
              <div className="col-6 col-md-4 form-check">
                <input
                  id="f-published"
                  className="form-check-input"
                  type="checkbox"
                  checked={onlyPublished}
                  onChange={(e) => setOnlyPublished(e.target.checked)}
                />
                <label htmlFor="f-published" className="form-check-label">Solo pubblicate nello spazio clienti</label>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Error banner */}
      {error && <div className="alert alert-danger">{error}</div>}

      {/* Tabella */}
      <div className="card shadow-sm">
        <div className="table-responsive">
          <table className="table align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th style={{ width: 70 }}>#</th>
                <th>Serie/N.</th>
                <th>Data</th>
                <th>Tipo</th>
                <th>Cliente</th>
                <th className="text-end" style={{ width: 140 }}>Totale</th>
                <th style={{ width: 180 }}>Stato</th>
                <th style={{ width: 110 }}>Pubblicata</th>
                <th style={{ width: 200 }}></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={9} className="text-center py-4">Caricamento…</td>
                </tr>
              ) : list.length === 0 ? (
                <tr>
                  <td colSpan={9} className="text-center py-4">Nessuna proforma trovata</td>
                </tr>
              ) : (
                list.map((f) => {
                  const td = (f.tipo_documento || "").toUpperCase();
                  const tdLabel = td === "TD04" ? "Nota di credito" : td === "TD01" ? "Proforma" : td || "—";
                  return (
                    <tr key={f.id} className={td === "TD04" ? "table-warning" : ""}>
                      <td>{f.id}</td>
                      <td><strong>{f.series}/{f.number ?? "—"}</strong></td>
                      <td>{formatDateIT(f.date)}</td>
                      <td>
                        <span className={`badge ${td === "TD04" ? "bg-warning text-dark" : "bg-light text-dark"}`}>
                          {tdLabel}
                        </span>
                      </td>
                      <td>{f.fatt_ragione_sociale || "—"}</td>
                      <td className="text-end">€ {Number(f.total || 0).toFixed(2)}</td>
                      <td>
                          {String(f.status || "").toUpperCase() === "DRAFT" ? (
                            <span className="badge bg-light text-dark border me-2" title="Modificabile">✏️</span>
                          ) : (
                            <span className="badge bg-light text-dark border me-2" title="Bloccata">🔒</span>
                          )}
                          <span className={badgeClass(f.status)}>{statoLabel(f.status)}</span>
                          {f.is_paid && <span className="badge bg-success ms-2">Pagata</span>}
                        </td>
                      <td>{f.is_published ? "✅" : "—"}</td>
                      <td className="text-end d-flex gap-2 justify-content-end">
                        <button className="btn btn-sm btn-outline-primary" onClick={() => nav(`/fatture/${f.id}`)} title="Apri editor">Apri</button>
                        {String(f.status || "").toUpperCase() === "DRAFT" && (
                          <button className="btn btn-sm btn-outline-danger" onClick={() => deleteInvoice(f)} title="Elimina bozza">Elimina</button>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Paginazione */}
        <div className="card-body border-top">
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
            <div className="text-muted small">
              {count} risultati • Pagina {Math.min(page, Math.max(1, Math.ceil((count || 0) / pageSize)))} di {Math.max(1, Math.ceil((count || 0) / pageSize))}
            </div>
            <div className="btn-group">
              <button
                className="btn btn-outline-secondary"
                disabled={page <= 1 || loading}
                onClick={() => { setPage((p) => Math.max(1, p - 1)); setTimeout(load, 0); }}
              >
                ← Precedente
              </button>
              <button
                className="btn btn-outline-secondary"
                disabled={page >= Math.max(1, Math.ceil((count || 0) / pageSize)) || loading}
                onClick={() => { setPage((p) => p + 1); setTimeout(load, 0); }}
              >
                Successiva →
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* MODALE ORDINI (quickpick) */}
      {showOrderModal && (
        <Modal title="Seleziona un ordine da trasformare in proforma" onClose={() => setShowOrderModal(false)}>
          <div className="row g-2 align-items-end mb-3">
            <div className="col-12 col-md-6">
              <label className="form-label small text-muted">Ricerca</label>
              <input
                className="form-control"
                placeholder="Cliente / etichetta / numero"
                value={oQ}
                onChange={(e) => setOQ(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && loadOrders()}
              />
            </div>
            <div className="col-6 col-md-3">
              <label className="form-label small text-muted">Stato</label>
              <select className="form-select" value={oStatus} onChange={(e) => setOStatus(e.target.value)}>
                <option value="">Tutti</option>
                <option value="DRAFT">Bozza</option>
                <option value="SUBMITTED">Inviato</option>
                <option value="APPROVED">Approvato</option>
                <option value="FULFILLED">Evaso</option>
                <option value="CANCELED">Annullato</option>
              </select>
            </div>
            <div className="col-6 col-md-3 d-flex gap-2">
              <button className="btn btn-secondary w-100" onClick={() => loadOrders()}>Applica</button>
              <button
                className="btn btn-outline-secondary w-100"
                onClick={() => { setOQ(""); setOStatus(""); setOPage(1); setTimeout(() => loadOrders(), 0); }}
              >
                Reset
              </button>
            </div>
          </div>

          <div className="table-responsive border rounded">
            <table className="table align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th style={{ width: 70 }}>#</th>
                  <th>Data</th>
                  <th>Per / Cliente</th>
                  <th className="text-end" style={{ width: 140 }}>Totale</th>
                  <th style={{ width: 150 }}>Stato</th>
                  <th style={{ width: 110 }}></th>
                </tr>
              </thead>
              <tbody>
                {ordersLoading ? (
                  <tr><td colSpan={6} className="text-center py-4">Caricamento…</td></tr>
                ) : orders.length === 0 ? (
                  <tr><td colSpan={6} className="text-center py-4">Nessun ordine trovato</td></tr>
                ) : (
                  orders.map((o) => (
                    <tr key={o.id}>
                      <td>{o.id}</td>
                      <td>{o.date ? String(o.date).slice(0, 10) : "—"}</td>
                      <td>{o.customer || o.per_nome || o.fatt_ragione_sociale || "—"}</td>
                      <td className="text-end">€ {Number(o.total || o.subtotal || 0).toFixed(2)}</td>
                      <td><span className="badge bg-secondary">{o.status}</span></td>
                      <td className="text-end">
                        <button className="btn btn-sm btn-primary" onClick={() => createFromOrder(o.id)}>Usa</button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="d-flex justify-content-between align-items-center mt-3">
            <div className="text-muted small">{ordersCount} risultati</div>
            <div className="d-flex gap-2">
              <select
                className="form-select"
                style={{ width: 90 }}
                value={oPageSize}
                onChange={(e) => { setOPageSize(Number(e.target.value) || 10); setOPage(1); setTimeout(() => loadOrders(), 0); }}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
              <div className="btn-group">
                <button
                  className="btn btn-outline-secondary"
                  disabled={oPage <= 1 || ordersLoading}
                  onClick={() => { setOPage((p) => Math.max(1, p - 1)); setTimeout(() => loadOrders(), 0); }}
                >
                  ←
                </button>
                <button
                  className="btn btn-outline-secondary"
                  disabled={!oHasMore || ordersLoading}
                  onClick={() => { setOPage((p) => p + 1); setTimeout(() => loadOrders(), 0); }}
                >
                  →
                </button>
              </div>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
